(() => {
  if (!customElements.get('lk-product-gallery')) {
    class LkProductGallery extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('[data-lk-gallery-viewport]')
        this.track = this.querySelector('[data-lk-gallery-track]')
        this.originalSlides = Array.from(this.querySelectorAll('[data-lk-gallery-slide]:not(.is-clone)'))
        this.thumbnailViewport = this.querySelector('.lk-gallery__thumbnails')
        this.thumbnails = Array.from(this.querySelectorAll('[data-lk-gallery-thumbnail]:not(.is-clone)'))
        this.current = Math.max(0, this.originalSlides.findIndex((slide) => slide.classList.contains('is-current')))

        if (!this.viewport || !this.track || !this.originalSlides.length) return

        this.onResize = this.position.bind(this, false)
        this.onVariantChange = this.handleVariantChange.bind(this)

        if (this.originalSlides.length > 1) {
          this.addLoopClones()
          this.addThumbnailLoopClones()
          this.querySelector('[data-lk-gallery-previous]')?.addEventListener('click', () => this.step(-1))
          this.querySelector('[data-lk-gallery-next]')?.addEventListener('click', () => this.step(1))
          this.bindSwipe()
        }

        ;(this.thumbnailItems || this.thumbnails).forEach((thumbnail, visualIndex) => {
          thumbnail.addEventListener('click', () => this.goTo(Number(thumbnail.dataset.lkGalleryThumbnail), visualIndex))
        })

        const root = this.closest('[data-section-id][data-product-id]')
        if (root) {
          this.variantEventName = `variant:change:${root.dataset.sectionId}:${root.dataset.productId}`
          document.addEventListener(this.variantEventName, this.onVariantChange)
        }

        window.addEventListener('resize', this.onResize, { passive: true })
        requestAnimationFrame(() => {
          this.position(true)
          this.positionThumbnail(true)
        })
        this.updateState(false)
        this.bindZoom()
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize)
        if (this.variantEventName) document.removeEventListener(this.variantEventName, this.onVariantChange)
        this.cancelThumbnailScrollReset()
        this.unlockPageScroll?.()
      }

      addLoopClones() {
        this.firstClone = this.originalSlides[0].cloneNode(true)
        this.lastClone = this.originalSlides[this.originalSlides.length - 1].cloneNode(true)
        this.firstClone.classList.add('is-clone')
        this.lastClone.classList.add('is-clone')
        this.firstClone.setAttribute('aria-hidden', 'true')
        this.lastClone.setAttribute('aria-hidden', 'true')
        this.firstClone.querySelectorAll('a, button, input, video, iframe').forEach((node) => node.setAttribute('tabindex', '-1'))
        this.lastClone.querySelectorAll('a, button, input, video, iframe').forEach((node) => node.setAttribute('tabindex', '-1'))
        this.track.prepend(this.lastClone)
        this.track.append(this.firstClone)
        this.slides = Array.from(this.track.children)
      }

      addThumbnailLoopClones() {
        if (!this.thumbnailViewport || this.thumbnails.length < 2) {
          this.thumbnailItems = this.thumbnails
          this.thumbnailVisualIndex = this.current
          return
        }

        const prepareClone = (thumbnail) => {
          const clone = thumbnail.cloneNode(true)
          clone.classList.add('is-clone')
          clone.setAttribute('aria-hidden', 'true')
          clone.setAttribute('tabindex', '-1')
          clone.removeAttribute('id')
          clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'))
          clone.querySelectorAll('a, button, input').forEach((node) => node.setAttribute('tabindex', '-1'))
          return clone
        }

        const before = this.thumbnails.map(prepareClone)
        const after = this.thumbnails.map(prepareClone)
        this.thumbnailViewport.prepend(...before)
        this.thumbnailViewport.append(...after)
        this.thumbnailItems = Array.from(this.thumbnailViewport.querySelectorAll('[data-lk-gallery-thumbnail]'))
        this.thumbnailVisualIndex = this.thumbnails.length + this.current
      }

      position(immediate = false, visualIndex = this.current + (this.originalSlides.length > 1 ? 1 : 0)) {
        const slide = this.slides ? this.slides[visualIndex] : this.originalSlides[visualIndex]
        if (!slide) return
        if (immediate) this.track.classList.add('is-jumping')
        else this.track.classList.remove('is-jumping')
        const target = (this.viewport.clientWidth / 2) - (slide.offsetLeft + slide.offsetWidth / 2)
        this.track.style.transform = `translate3d(${target}px, 0, 0)`
        if (immediate) {
          void this.track.offsetWidth
          requestAnimationFrame(() => this.track.classList.remove('is-jumping'))
        }
      }

      step(direction) {
        const count = this.originalSlides.length
        const next = this.current + direction
        const thumbnailTarget = Number.isFinite(this.thumbnailVisualIndex) ? this.thumbnailVisualIndex + direction : this.current + count + direction

        if (next < 0) {
          this.current = count - 1
          this.position(false, 0)
          this.afterTransition(() => this.position(true, count))
        } else if (next >= count) {
          this.current = 0
          this.position(false, count + 1)
          this.afterTransition(() => this.position(true, 1))
        } else {
          this.current = next
          this.position(false)
        }

        this.moveThumbnail(thumbnailTarget)
        this.updateState(false)
      }

      goTo(index, thumbnailVisualIndex) {
        if (!Number.isFinite(index) || index < 0 || index >= this.originalSlides.length) return
        this.current = index
        this.position(false)
        this.moveThumbnail(Number.isFinite(thumbnailVisualIndex) ? thumbnailVisualIndex : this.thumbnails.length + index)
        this.updateState(false)
      }

      afterTransition(callback) {
        let timeout
        const done = (event) => {
          if (event && (event.target !== this.track || event.propertyName !== 'transform')) return
          this.track.removeEventListener('transitionend', done)
          window.clearTimeout(timeout)
          callback()
        }
        this.track.addEventListener('transitionend', done, { once: true })
        timeout = window.setTimeout(done, 650)
      }

      positionThumbnail(immediate = false, visualIndex = this.thumbnailVisualIndex) {
        const thumbnail = this.thumbnailItems?.[visualIndex] || this.thumbnails[visualIndex]
        if (!this.thumbnailViewport || !thumbnail) return
        const left = thumbnail.offsetLeft - (this.thumbnailViewport.clientWidth - thumbnail.offsetWidth) / 2
        this.thumbnailViewport.scrollTo({ left, behavior: immediate ? 'auto' : 'smooth' })
      }

      moveThumbnail(visualIndex) {
        if (!this.thumbnailItems?.length || !Number.isFinite(visualIndex)) return
        this.cancelThumbnailScrollReset()
        this.thumbnailVisualIndex = visualIndex
        this.positionThumbnail(false, visualIndex)

        const count = this.thumbnails.length
        let resetIndex = null
        if (visualIndex < count) resetIndex = visualIndex + count
        else if (visualIndex >= count * 2) resetIndex = visualIndex - count
        if (resetIndex === null) return

        this.afterThumbnailScroll(() => {
          this.thumbnailVisualIndex = resetIndex
          this.positionThumbnail(true, resetIndex)
          this.updateState(false)
        })
      }

      afterThumbnailScroll(callback) {
        const done = () => {
          this.cancelThumbnailScrollReset()
          callback()
        }
        this.thumbnailScrollDone = done
        this.thumbnailViewport.addEventListener('scrollend', done, { once: true })
        this.thumbnailScrollTimer = window.setTimeout(done, 650)
      }

      cancelThumbnailScrollReset() {
        if (this.thumbnailScrollDone && this.thumbnailViewport) {
          this.thumbnailViewport.removeEventListener('scrollend', this.thumbnailScrollDone)
        }
        window.clearTimeout(this.thumbnailScrollTimer)
        this.thumbnailScrollDone = null
        this.thumbnailScrollTimer = null
      }

      updateState(scrollThumbnail = true) {
        this.originalSlides.forEach((slide, index) => {
          const current = index === this.current
          slide.classList.toggle('is-current', current)
          slide.setAttribute('aria-hidden', current ? 'false' : 'true')
        })

        this.firstClone?.classList.toggle('is-current', this.current === 0)
        this.lastClone?.classList.toggle('is-current', this.current === this.originalSlides.length - 1)

        ;(this.thumbnailItems || this.thumbnails).forEach((thumbnail, visualIndex) => {
          thumbnail.classList.toggle('is-current', visualIndex === this.thumbnailVisualIndex)
          if (thumbnail.classList.contains('is-clone')) thumbnail.removeAttribute('aria-current')
          else thumbnail.setAttribute('aria-current', Number(thumbnail.dataset.lkGalleryThumbnail) === this.current ? 'true' : 'false')
        })

        if (scrollThumbnail) {
          this.moveThumbnail(this.thumbnails.length + this.current)
        }
      }

      bindSwipe() {
        let startX = null
        this.viewport.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return
          startX = event.clientX
        })
        this.viewport.addEventListener('pointerup', (event) => {
          if (startX === null) return
          const distance = event.clientX - startX
          startX = null
          if (Math.abs(distance) > 45) this.step(distance > 0 ? -1 : 1)
        })
        this.viewport.addEventListener('pointercancel', () => { startX = null })
      }

      bindZoom() {
        this.lightbox = this.querySelector('[data-lk-gallery-lightbox]')
        this.lightboxImage = this.querySelector('[data-lk-lightbox-image]')
        this.lightboxStage = this.querySelector('[data-lk-lightbox-stage]')
        this.zoomableSlides = this.originalSlides.filter((slide) => slide.dataset.lkGalleryZoomUrl)
        if (!this.lightbox || !this.lightboxImage || !this.zoomableSlides.length) return

        this.track.addEventListener('click', (event) => {
          if (!(event.target instanceof HTMLImageElement)) return
          const slide = event.target.closest('[data-lk-gallery-slide][data-lk-gallery-zoom-url]')
          if (!slide) return
          this.openZoom(Number(slide.dataset.index))
        })

        this.querySelector('[data-lk-lightbox-close]')?.addEventListener('click', () => this.closeZoom())
        this.querySelector('[data-lk-lightbox-previous]')?.addEventListener('click', () => this.stepZoom(-1))
        this.querySelector('[data-lk-lightbox-next]')?.addEventListener('click', () => this.stepZoom(1))

        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.closeZoom()
        })

        this.lightbox.addEventListener('close', () => {
          this.lightbox.classList.remove('is-zoomed')
          this.lightboxStage?.scrollTo(0, 0)
          this.unlockPageScroll()
        })

        this.lightboxImage.addEventListener('click', () => {
          this.lightbox.classList.toggle('is-zoomed')
          if (!this.lightbox.classList.contains('is-zoomed')) this.lightboxStage?.scrollTo(0, 0)
        })
      }

      openZoom(index) {
        const slide = this.originalSlides[index]
        if (!slide?.dataset.lkGalleryZoomUrl || !this.lightbox || !this.lightboxImage) return
        this.lightboxIndex = index
        this.lightbox.classList.remove('is-zoomed')
        this.lightboxImage.src = slide.dataset.lkGalleryZoomUrl
        this.lightboxImage.alt = slide.dataset.lkGalleryZoomAlt || ''
        this.lightboxStage?.scrollTo(0, 0)
        if (!this.lightbox.open) {
          this.lockPageScroll()
          this.lightbox.showModal()
        }
      }

      closeZoom() {
        if (this.lightbox?.open) this.lightbox.close()
      }

      lockPageScroll() {
        if (document.body.classList.contains('lk-gallery-lightbox-open')) return
        this.pageScrollY = window.scrollY
        document.documentElement.classList.add('lk-gallery-lightbox-open')
        document.body.classList.add('lk-gallery-lightbox-open')
        document.body.style.top = `-${this.pageScrollY}px`
      }

      unlockPageScroll() {
        if (!document.body.classList.contains('lk-gallery-lightbox-open')) return
        const scrollY = Number(this.pageScrollY || 0)
        document.documentElement.classList.remove('lk-gallery-lightbox-open')
        document.body.classList.remove('lk-gallery-lightbox-open')
        document.body.style.removeProperty('top')
        window.scrollTo(0, scrollY)
      }

      stepZoom(direction) {
        if (!this.zoomableSlides?.length) return
        const currentPosition = Math.max(0, this.zoomableSlides.findIndex((slide) => Number(slide.dataset.index) === this.lightboxIndex))
        const nextPosition = (currentPosition + direction + this.zoomableSlides.length) % this.zoomableSlides.length
        this.openZoom(Number(this.zoomableSlides[nextPosition].dataset.index))
      }

      handleVariantChange(event) {
        const mediaId = event.detail?.variant?.featured_media?.id
        if (!mediaId) return
        const index = this.originalSlides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId))
        if (index >= 0) this.goTo(index)
      }
    }

    customElements.define('lk-product-gallery', LkProductGallery)
  }

  const initStickyCart = (root) => {
    if (!root || root.dataset.lkStickyReady === 'true') return
    root.dataset.lkStickyReady = 'true'

    const purchase = root.querySelector('[data-lk-purchase]')
    const sticky = root.querySelector('[data-lk-sticky-cart]')
    const stickyAdd = root.querySelector('[data-lk-sticky-add]')
    const stickyQuantity = root.querySelector('[data-lk-sticky-quantity]')
    const stickyInventory = root.querySelector('[data-lk-sticky-inventory]')
    const stickyInventoryText = root.querySelector('[data-lk-sticky-inventory-text]')
    const stickyTitleWindow = root.querySelector('[data-lk-sticky-title-window]')
    const stickyTitle = root.querySelector('[data-lk-sticky-title]')
    const expressBadge = root.querySelector('[data-lk-express-badge]')
    const ready24 = root.querySelector('[data-lk-ready-24]')
    if (!purchase || !sticky || !stickyAdd || !stickyQuantity) return

    const primaryButton = () => purchase.querySelector('button[name="add"]')
    const primaryQuantity = () => purchase.querySelector('input[name="quantity"]')
    let inventoryMap = {}

    try {
      inventoryMap = JSON.parse(root.querySelector('[data-lk-inventory-map]')?.textContent || '{}')
    } catch (error) {
      inventoryMap = {}
    }

    const syncTitleAnimation = () => {
      if (!stickyTitleWindow || !stickyTitle) return
      stickyTitleWindow.classList.remove('is-scrolling')
      stickyTitleWindow.style.removeProperty('--lk-title-travel')
      stickyTitleWindow.style.removeProperty('--lk-title-duration')

      const distance = Math.ceil(stickyTitle.scrollWidth - stickyTitleWindow.clientWidth)
      if (distance <= 2) return

      stickyTitleWindow.style.setProperty('--lk-title-travel', `-${distance}px`)
      stickyTitleWindow.style.setProperty('--lk-title-duration', `${Math.max(7, distance / 22 + 5)}s`)
      stickyTitleWindow.classList.add('is-scrolling')
    }

    const syncInventory = () => {
      if (!stickyInventory || !stickyInventoryText) return
      const standardInventory = root.querySelector('[data-lk-standard-inventory]')
      const customInventory = root.querySelector('[data-lk-custom-inventory]')
      const customInventoryText = root.querySelector('[data-lk-custom-inventory-text]')
      const sourceText = standardInventory?.querySelector('[data-product-inventory]')
      const standardSource = sourceText?.closest('.icon-and-text')
      const variantJson = root.querySelector('.lk-product-page__inventory [data-current-variant-json]')
      let variantId = String(purchase.querySelector('input[name="id"]')?.value || '')

      if (!variantId) {
        try {
          variantId = String(JSON.parse(variantJson?.textContent || '{}').id || '')
        } catch (error) {
          variantId = ''
        }
      }

      const inventory = inventoryMap[variantId]
      const fallbackText = sourceText?.textContent.trim() || ''
      const fallbackQuantity = /auf lager/i.test(fallbackText) ? fallbackText.match(/-?\d+/)?.[0] : null
      const hasExactQuantity = Boolean(inventory?.tracked && Number.isFinite(Number(inventory.quantity)))
      const quantity = hasExactQuantity ? Number(inventory.quantity) : (fallbackQuantity == null ? null : Number(fallbackQuantity))
      const isInStock = Boolean(inventory?.tracked && Number(inventory.quantity) > 0)
      if (expressBadge) expressBadge.hidden = !isInStock
      if (ready24) ready24.hidden = !isInStock
      const customDeliveryTime = root.dataset.lkCustomDeliveryTime?.trim() || '1-2 Wochen'
      const lkLocale = (document.documentElement.lang || 'de').split('-')[0].toLowerCase()
      const lkStockLabel = { en: 'in stock', fr: 'en stock', es: 'en stock', de: 'auf Lager' }[lkLocale] || 'auf Lager'
      const lkUnits = {
        en: { werktage: 'business days', wochen: 'weeks', woche: 'week', tage: 'days', tag: 'day' },
        fr: { werktage: 'jours ouvrables', wochen: 'semaines', woche: 'semaine', tage: 'jours', tag: 'jour' },
        es: { werktage: 'días laborables', wochen: 'semanas', woche: 'semana', tage: 'días', tag: 'día' }
      }
      const lkDeliveryTime = customDeliveryTime.replace(/\b(Werktage|Wochen|Woche|Tage|Tag)\b/gi, (part) => lkUnits[lkLocale]?.[part.toLowerCase()] || part)
      let text = quantity == null ? fallbackText : (quantity > 5 ? `5+ ${lkStockLabel}` : `${Math.max(0, quantity)} ${lkStockLabel}`)
      let status = 'available'

      if (quantity != null) {
        if (quantity <= 0) status = 'out'
        else if (quantity <= 3) status = 'low'
      } else if (/nicht auf lager|ausverkauft|lieferzeit/i.test(fallbackText)) {
        status = 'out'
      } else if (standardSource?.classList.contains('inventory--low')) {
        status = 'low'
      }

      if (status === 'out') {
        const lkOutLabel = {
          en: 'On order, delivery in approx.',
          fr: 'En réapprovisionnement, livraison sous env.',
          es: 'Bajo pedido, entrega en aprox.',
          de: 'Nachbestellt, Lieferzeit ca.'
        }[lkLocale] || 'Nachbestellt, Lieferzeit ca.'
        text = `${lkOutLabel} ${lkDeliveryTime}`
      }

      const useCustomInventory = status === 'out' || !sourceText
      if (standardInventory) standardInventory.hidden = useCustomInventory
      if (customInventory) customInventory.hidden = !useCustomInventory
      if (customInventoryText && customInventoryText.textContent.trim() !== text) customInventoryText.textContent = text

      const source = useCustomInventory ? customInventory : standardSource

      const statusClasses = ['lk-inventory--out', 'lk-inventory--low', 'lk-inventory--available']
      const nextStatusClass = `lk-inventory--${status}`

      if (source) {
        const currentStatusClasses = statusClasses.filter((className) => source.classList.contains(className))
        const statusChanged = currentStatusClasses.length !== 1 || currentStatusClasses[0] !== nextStatusClass

        if (statusChanged) {
          source.classList.remove(...statusClasses)
          source.classList.add(nextStatusClass)
        }
      }

      Array.from(stickyInventory.classList)
        .filter((className) => className.startsWith('inventory--') || className.startsWith('lk-inventory--'))
        .forEach((className) => stickyInventory.classList.remove(className))

      source?.classList.forEach((className) => {
        if (className.startsWith('inventory--')) stickyInventory.classList.add(className)
      })

      stickyInventory.classList.add(`lk-inventory--${status}`)

      if (!useCustomInventory && sourceText && sourceText.textContent.trim() !== text) sourceText.textContent = text
      stickyInventoryText.textContent = text
      stickyInventory.hidden = !text
      syncInventoryLayout()
    }

    const syncInventoryLayout = () => {
      if (!stickyInventory || !stickyInventoryText) return
      stickyInventory.classList.remove('is-two-line')
      if (stickyInventory.hidden) return

      const needsTwoLines = stickyInventory.scrollWidth > sticky.clientWidth * 0.5
      stickyInventory.classList.toggle('is-two-line', needsTwoLines)
    }

    const sync = () => {
      const button = primaryButton()
      const quantity = primaryQuantity()
      if (button) {
        stickyAdd.textContent = button.textContent.trim()
        stickyAdd.disabled = button.disabled
      }
      stickyQuantity.textContent = quantity?.value || '1'
      syncInventory()
      requestAnimationFrame(syncTitleAnimation)
    }

    const changeQuantity = (difference) => {
      const input = primaryQuantity()
      if (!input) return
      const min = Number(input.min || 1)
      const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY
      const current = Number(input.value || min)
      input.value = String(Math.min(max, Math.max(min, current + difference)))
      input.dispatchEvent(new Event('input', { bubbles: true }))
      input.dispatchEvent(new Event('change', { bubbles: true }))
      sync()
    }

    root.querySelector('[data-lk-sticky-minus]')?.addEventListener('click', () => changeQuantity(-1))
    root.querySelector('[data-lk-sticky-plus]')?.addEventListener('click', () => changeQuantity(1))
    stickyAdd.addEventListener('click', () => {
      const button = primaryButton()
      if (button && !button.disabled) button.click()
    })

    purchase.addEventListener('input', sync)
    purchase.addEventListener('change', sync)

    const mutationObserver = new MutationObserver(sync)
    mutationObserver.observe(purchase, { subtree: true, childList: true, attributes: true, characterData: true })

    const inventory = root.querySelector('.lk-product-page__inventory')
    if (inventory) {
      const inventoryObserver = new MutationObserver(syncInventory)
      inventoryObserver.observe(inventory, { subtree: true, childList: true, characterData: true })
    }

    window.addEventListener('resize', syncTitleAnimation, { passive: true })
    window.addEventListener('resize', syncInventoryLayout, { passive: true })
    document.fonts?.ready.then(syncTitleAnimation)

    let purchasePassed = false
    const footer = document.querySelector('.shopify-section-group-footer-group')

    const updateVisibility = () => {
      const footerRect = footer?.getBoundingClientRect()
      const footerReached = Boolean(footerRect && footerRect.top < document.documentElement.clientHeight)
      const show = purchasePassed && !footerReached
      sticky.classList.toggle('is-visible', show)
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true')
    }

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      purchasePassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0
      updateVisibility()
    }, { threshold: 0 })
    intersectionObserver.observe(purchase)

    if (footer) {
      window.addEventListener('scroll', updateVisibility, { passive: true })
    }

    sync()
  }

  const initMorePaymentOptions = (root) => {
    if (!root || root.dataset.lkPaymentOptionsReady === 'true') return
    root.dataset.lkPaymentOptionsReady = 'true'

    const trigger = root.querySelector('[data-lk-more-payment-options]')
    if (!trigger) return

    trigger.addEventListener('click', () => {
      root.querySelector('.shopify-payment-button__more-options')?.click()
    })
  }

  const initOfferButton = (root) => {
    if (!root || root.dataset.lkOfferButtonReady === 'true') return
    root.dataset.lkOfferButtonReady = 'true'

    const apps = root.querySelector('.lk-product-page__apps')
    if (!apps) return

    const update = () => {
      const button = apps.querySelector('#ma-make-an-offer-open-button')
      if (!button) return

      const labelNode = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim())
      if (labelNode && labelNode.textContent.trim() !== 'Wunschpreis anfragen') {
        labelNode.textContent = 'Wunschpreis anfragen'
      }
      button.setAttribute('aria-label', 'Wunschpreis anfragen')
    }

    const observer = new MutationObserver(update)
    observer.observe(apps, { subtree: true, childList: true })
    update()
  }

  const initDetailsAlignment = (root) => {
    if (!root || root.dataset.lkDetailsAlignmentReady === 'true') return
    root.dataset.lkDetailsAlignmentReady = 'true'

    const update = () => {
      const inner = root.querySelector('.lk-product-page__inner')
      const details = document.querySelector('.product-full-width-video')
      if (!inner || !details) return

      const innerRect = inner.getBoundingClientRect()
      details.style.setProperty('--lk-details-left', `${Math.max(12, innerRect.left)}px`)
      details.style.setProperty('--lk-details-right', `${Math.max(12, document.documentElement.clientWidth - innerRect.right)}px`)
    }

    window.addEventListener('resize', update, { passive: true })
    requestAnimationFrame(update)
  }

  const initPriceAlignment = (root) => {
    if (!root || root.dataset.lkPriceAlignmentReady === 'true') return
    root.dataset.lkPriceAlignmentReady = 'true'

    const heading = root.querySelector('.lk-product-page__heading')
    const title = root.querySelector('.lk-product-page__title h1')
    const priceArea = root.querySelector('[data-lk-price-area]')
    if (!heading || !title || !priceArea) return

    const update = () => {
      if (document.documentElement.clientWidth <= 1199) {
        priceArea.style.removeProperty('--lk-price-offset')
        return
      }

      priceArea.style.setProperty('--lk-price-offset', '0px')
      const headingRect = heading.getBoundingClientRect()
      const titleRect = title.getBoundingClientRect()
      const priceLine = priceArea.classList.contains('has-live-price-check')
        ? Array.from(priceArea.querySelectorAll('.block-price__sale, .block-price__regular')).find((line) => line.offsetHeight > 0)
        : priceArea
      const alignmentHeight = priceLine?.offsetHeight || priceArea.offsetHeight
      const offset = Math.max(0, titleRect.bottom - headingRect.top - alignmentHeight)
      priceArea.style.setProperty('--lk-price-offset', `${offset}px`)
    }

    window.addEventListener('resize', update, { passive: true })
    document.fonts?.ready.then(update)
    requestAnimationFrame(update)
  }


  const initAccessoriesPlacement = (root) => {
    if (!root || root.dataset.lkAccessoriesReady === 'true') return
    root.dataset.lkAccessoriesReady = 'true'

    const accessories = root.querySelector('[data-lk-accessories]')
    if (!accessories) return

    const details = document.querySelector('.product-full-width-video')
    const videoColumn = details?.querySelector('.product-full-width-video__video-column')
    const detailsContent = details?.querySelector('.product-full-width-video-wrapper .page-width')
    const target = videoColumn || detailsContent
    if (target) target.append(accessories)

    const findManualTipoWidget = () => Array.from(document.querySelectorAll('.ga-products-box')).find((widget) => {
      const title = widget.dataset.title || widget.querySelector('.ga-title')?.textContent || ''
      return title.trim().toLocaleLowerCase('de-DE') === 'zubehör'
    })

    const placeManualTipoWidget = () => {
      const widget = findManualTipoWidget()
      if (!widget) return false
      if (widget.parentElement !== accessories) accessories.append(widget)
      accessories.classList.add('has-products')
      return true
    }

    if (placeManualTipoWidget()) return

    const observer = new MutationObserver(() => {
      if (placeManualTipoWidget()) observer.disconnect()
    })
    observer.observe(document.body, { subtree: true, childList: true })
    window.setTimeout(() => observer.disconnect(), 10000)
  }

  const initVideoScroll = (root) => {
    if (!root || root.dataset.lkVideoScrollReady === 'true') return
    root.dataset.lkVideoScrollReady = 'true'

    const button = root.querySelector('[data-lk-video-button]')
    if (!button) return

    button.addEventListener('click', (event) => {
      const targetId = (button.getAttribute('href') || '').replace(/^#/, '')
      const target = targetId ? document.getElementById(targetId) : null
      if (!target) return

      event.preventDefault()
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      })
    })
  }

  const initRoot = (root) => {
    initStickyCart(root)
    initMorePaymentOptions(root)
    initOfferButton(root)
    initDetailsAlignment(root)
    initPriceAlignment(root)
    initVideoScroll(root)
    initAccessoriesPlacement(root)
  }

  const initAll = () => document.querySelectorAll('.lk-product-page').forEach(initRoot)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true })
  } else {
    initAll()
  }

  document.addEventListener('shopify:section:load', (event) => initRoot(event.target.querySelector('.lk-product-page')))
})()
